import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactGA from 'react-ga4'
import { useNavigate } from 'react-router-dom'
import Footer from 'components/Footer'
import Header from 'components/Header'
import useLockBodyScroll from 'hooks/useLockBodyScroll'
import useQueryParams from 'hooks/useQueryParams'
import { createNavigateWithQueryParams } from 'utils/navigateWithQueryParams'
import styles from './intro.module.scss'

const VIDEO_URL = 'https://storage.googleapis.com/kiro-production-49817cb/KIRO%20Intro%20Video.mp4'

const Intro = () => {
  const { getAllParams } = useQueryParams()
  const navigate = useNavigate()

  // Create reusable navigation function
  const navigateWithQueryParams = createNavigateWithQueryParams(navigate, getAllParams)

  useLockBodyScroll()

  const videoRef = useRef<HTMLVideoElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  // The first unmute restarts the clip so the visitor hears it from the top.
  const heardFromStartRef = useRef(false)
  const [muted, setMuted] = useState(false)

  useEffect(() => {
    ReactGA.event('page_view', {
      page_title: 'Intro',
      page_location: '/intro',
    })
  }, [])

  // Start with sound on. Browsers block unmuted autoplay unless the visitor has
  // already engaged with the site, so fall back to muted playback rather than a
  // stalled frame.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    let cancelled = false

    const playMuted = () => {
      if (cancelled) return
      video.muted = true
      setMuted(true)
      const retry = video.play()
      if (retry && typeof retry.catch === 'function') {
        retry.catch(() => undefined)
      }
    }

    video.muted = false
    const promise = video.play()
    if (promise && typeof promise.catch === 'function') {
      promise.catch(playMuted)
    }

    return () => {
      cancelled = true
    }
  }, [])

  // Browsers only lift the sound block once the visitor has interacted, so take
  // the very first gesture anywhere on the page — not just on the video — and
  // restore audio with it. Gestures inside the card are skipped; that element
  // has its own toggle and would otherwise be handled twice.
  useEffect(() => {
    const restoreSound = (event: Event) => {
      const video = videoRef.current
      if (!video || !video.muted) return
      if (cardRef.current && event.target instanceof Node && cardRef.current.contains(event.target)) return

      video.muted = false
      setMuted(false)
      if (!heardFromStartRef.current) {
        heardFromStartRef.current = true
        video.currentTime = 0
      }
      const promise = video.play()
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => undefined)
      }
    }

    const events: Array<keyof DocumentEventMap> = ['pointerdown', 'touchstart', 'keydown']
    events.forEach((name) => document.addEventListener(name, restoreSound, true))
    return () => events.forEach((name) => document.removeEventListener(name, restoreSound, true))
  }, [])

  // The video never pauses — tapping it, like the corner icon, only toggles sound.
  const toggleSound = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    const nextMuted = !video.muted
    video.muted = nextMuted
    setMuted(nextMuted)

    if (!nextMuted && !heardFromStartRef.current) {
      heardFromStartRef.current = true
      video.currentTime = 0
    }

    if (video.paused) {
      const promise = video.play()
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => undefined)
      }
    }
  }, [])

  const handleToggleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      toggleSound()
    },
    [toggleSound],
  )

  return (
    <div className={styles.container}>
      <Header headerText={'Welcome to KIRO'} showBackButton={false}></Header>

      <div className={styles.videoCard} ref={cardRef} onClick={toggleSound}>
        <video
          className={styles.video}
          ref={videoRef}
          src={VIDEO_URL}
          autoPlay
          loop
          playsInline
          preload="auto"
          controls={false}
          controlsList="nodownload"
          disablePictureInPicture
        />

        <button
          type="button"
          className={styles.soundToggle}
          onClick={handleToggleClick}
          aria-label={muted ? 'Unmute video' : 'Mute video'}
        >
          {muted ? (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M11 5 6.5 9H3v6h3.5L11 19V5z" />
              <path d="M16.5 9.5 15 11l1.5 1.5L15 14l1.5 1.5L18 14l1.5 1.5L21 14l-1.5-1.5L21 11l-1.5-1.5L18 11l-1.5-1.5z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M11 5 6.5 9H3v6h3.5L11 19V5z" />
              <path d="M15.5 8.5a5 5 0 0 1 0 7 1 1 0 0 0 1.4 1.4 7 7 0 0 0 0-9.8 1 1 0 1 0-1.4 1.4z" />
            </svg>
          )}
        </button>
      </div>

      <Footer
        buttonText={'Continue'}
        isLoading={false}
        // 0 drops the tracker row entirely (it renders invisible here anyway),
        // so the footer is just the button plus the 39px of padding that puts
        // it on the same line as signup home's primary button.
        trackerIndex={0}
        isDisabled={false}
        buttonType={'button'}
        style={{ paddingBottom: '39px', minHeight: 'auto' }}
        onButtonClick={() => {
          ReactGA.event('intro_cta_click', {
            page_title: 'Intro',
            page_location: '/intro',
          })
          navigateWithQueryParams('/signup')
        }}
      ></Footer>
    </div>
  )
}

export default Intro
